import React, { useEffect, useState } from 'react';
import { CalendarDays, Archive } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatClock, formatLocalDate } from '../../../lib/eventWorkspace';
import { errorText } from '../../../lib/apiErrors';

const todayLocal = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** Events a guest can move to: same org, not this one, not ended or cancelled, from today on. */
export function upcomingTargets(events, currentEventId) {
    return (Array.isArray(events) ? events : []).filter((item) => item.event_id !== currentEventId && !item.ended_at
        && !['cancelled', 'completed'].includes(item.status) && !item.is_deleted && (!item.date || item.date >= todayLocal()))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

/**
 * Reschedule a guest (D7): pick a future event. They join it in To invite with the same Contact Owner and
 * close here as "Rescheduled → <event · date>". "Send the invite now" opens the composer for the target
 * event after saving; its send moves them to Invited there. `onSubmit({target, sendInvite})`.
 */
export default function RescheduleGuestDialog({ open, onClose, onSubmit, guests = [], currentEventId, listEvents, busy = false }) {
    const [events, setEvents] = useState(null);
    const [error, setError] = useState('');
    const [targetId, setTargetId] = useState('');
    const [sendInvite, setSendInvite] = useState(true);
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setEvents(null); setError(''); setTargetId(''); setSendInvite(true);
        listEvents()
            .then((rows) => { if (!cancelled) setEvents(upcomingTargets(rows, currentEventId)); })
            .catch((err) => { if (!cancelled) { setEvents([]); setError(errorText(err, 'Could not load upcoming events')); } });
        return () => { cancelled = true; };
    }, [open, currentEventId, listEvents]);
    const target = (events || []).find((item) => item.event_id === targetId);
    const who = guests.length === 1 ? `${guests[0].name}${guests[0].title ? ` · ${guests[0].title}` : ''}${guests[0].company ? ` · ${guests[0].company}` : ''}` : `${guests.length} guests`;
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose?.(); }}>
            <DialogContent data-testid="reschedule-guest-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Reschedule to another event</DialogTitle>
                    <DialogDescription>{who}</DialogDescription>
                </DialogHeader>
                <ul className="space-y-2 text-sm text-stone-700">
                    <li className="flex items-start gap-2.5"><CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" aria-hidden="true" />They’re added to the event you pick, in To invite, with the same Contact Owner.</li>
                    <li className="flex items-start gap-2.5"><Archive className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" aria-hidden="true" />Here they close as “Rescheduled”, so this event’s numbers stay honest.</li>
                </ul>
                <fieldset className="space-y-1.5">
                    <legend className="mb-1 text-xs font-medium text-stone-600">Which event?</legend>
                    {events === null && <p className="text-sm text-stone-500">Loading upcoming events…</p>}
                    {events !== null && events.length === 0 && <p data-testid="reschedule-guest-empty" className="text-sm text-stone-500">{error || 'There is no upcoming event to move them to.'}</p>}
                    {(events || []).map((item) => (
                        <label key={item.event_id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${targetId === item.event_id ? 'border-stone-900 bg-stone-50' : 'border-stone-200'}`}>
                            <input type="radio" name="reschedule-target" value={item.event_id} checked={targetId === item.event_id} onChange={() => setTargetId(item.event_id)} className="accent-stone-900" />
                            <span className="min-w-0 flex-1 truncate">{item.name}<span className="text-stone-500">{` · ${formatLocalDate(item.date)}${item.start_time ? ` · ${formatClock(item.start_time)}` : ''}`}</span></span>
                        </label>
                    ))}
                </fieldset>
                <label className="flex items-start gap-2 text-sm text-stone-700">
                    <input type="checkbox" aria-label="Send the invite now" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="mt-0.5 h-4 w-4 accent-stone-900" />
                    <span>Send the invite now (they move to Invited there once it is sent)</span>
                </label>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="reschedule-guest-submit" disabled={busy || !target} onClick={() => onSubmit({ target, sendInvite })}>{busy ? 'Saving…' : 'Reschedule'}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
